import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Col, Row} from 'react-grid-system'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router-dom'
import {useMutation} from '@apollo/client'

import {
  LoadingSpinner,
  SimpleLabel,
  SimpleNote,
  StyledInput,
  ToggleSwitchWithLabel,
} from 'pyrexx-react-library'
import Navigator from '../../Navigator/Navigator'

import {
  toggleSwitchWithLabelTheme
} from '../../../styles/themes/ToggleSwitchWithLabel.theme'
import {
  SCHEDULE_APPOINTMENT
} from "../../../mutations/ScheduleAppointmentMutation";

const CustomerInput = (props) => {
  const {savedValues, prevStep, nextStep, maxSteps, currentStep} = props

  const {t} = useTranslation()
  const navigate = useNavigate()

  const [submitStatus, setSubmitStatus] = useState({
    loading: false,
    status: false,
    message: '',
  })

  const [scheduleAppointment] = useMutation(SCHEDULE_APPOINTMENT)

  const schema = Yup.object({
    contactName: Yup.string().required(t('PROVIDE A CONTACT NAME')),
    inquiryEmail: Yup.bool(),
    contactEmail: Yup.string().email(t('PROVIDE A CONTACT E-MAIL')),
    inquiryTel: Yup.bool(),
    contactTel: Yup.string(t('PROVIDE A PHONE NUMBER')),
    automaticEmailNotice: Yup.bool(),
  }).test('email-or-telephone', t('PROVIDE EITHER CONTACT E-MAIL OR PHONE NUMBER'), function (values) {
    const {contactTel, contactEmail, inquiryEmail, inquiryTel} = values;

    if (!contactTel && !contactEmail) {
      return this.createError({
        path: 'contactEmail',
        message: t('PROVIDE EITHER EMAIL OR TELEPHONE NUMBER'),
      });
    }

    if (!inquiryEmail && !inquiryTel) {
      return this.createError({
        path: 'contactEmail',
        message: t('SELECT EITHER EMAIL OR TELEPHONE NUMBER OPTION'),
      });
    }

    if (!inquiryEmail && contactEmail && !contactTel) {
      return this.createError({
        path: 'contactEmail',
        message: t('SELECT EMAIL OPTION AS YOU HAVE PROVIDED IT'),
      });
    }

    if (!inquiryTel && contactTel && !contactEmail) {
      return this.createError({
        path: 'contactTel',
        message: t('SELECT TELEPHONE NUMBER OPTION AS YOU HAVE PROVIDED IT'),
      });
    }
    return true;
  })

  const initialContactName =
    savedValues.firstName && savedValues.lastName
      ? savedValues.firstName + ' ' + savedValues.lastName
      : ''

  const formik = useFormik({
    initialValues: {
      validatedEncryptedApartmentId: '',
      contactName: initialContactName,
      inquiryEmail: false,
      contactEmail: '',
      inquiryTel: !!savedValues.tel,
      contactTel: savedValues.tel ? savedValues.tel : '',
    },
    validationSchema: schema,
    onSubmit: (values) => {
      setSubmitStatus((prevState) => ({...prevState, loading: true}))

      const appointmentSchedule = {
        // inhabitant data
        firstName: savedValues.firstName,
        lastName: savedValues.lastName,

        // location
        street: savedValues.street,
        floor: savedValues.floor,
        zip: savedValues.zip,
        town: savedValues.town,

        validatedEncryptedApartmentId: savedValues.validatedEncryptedApartmentId,
        appointmentId: savedValues.appointmentId,
        timeFrame: {
          from: savedValues.timeframeFrom ? savedValues.timeframeFrom : null,
          to: savedValues.timeframeTo ? savedValues.timeframeTo : null,
        },

        // contact info
        contactTel: values.contactTel,
        contactEmail: values.contactEmail,
        contactName: values.contactName,

        preferredContact: values.inquiryEmail ? 'EMAIL' : 'PHONE'
      }

      scheduleAppointment({
        variables: {appointmentSchedule},
        onCompleted: (data) => {
          if (data?.scheduleAppointment?.success) {
            setSubmitStatus({
              loading: false,
              status: 'success',
              message: t('SUCCESSFULLY SUBMITTED'),
            })
            navigate(nextStep)
          } else {
            setSubmitStatus({
              loading: false,
              status: 'error',
              message: t(
                'SOMETHING WENT WRONG, PLEASE TRY AGAIN LATER OR CONTACT THE SUPPORT',
              ),
            })
          }
        },
        onError: () => {
          setSubmitStatus({
            loading: false,
            status: 'error',
            message: t(
              'SOMETHING WENT WRONG, PLEASE TRY AGAIN LATER OR CONTACT THE SUPPORT',
            ),
          })
        },
      })
    },
  })


  return (
    <form onSubmit={formik.handleSubmit}>
      {submitStatus.loading && <LoadingSpinner/>}

      <Row justify='center'>
        <Col xs={12} md={12}>
          <StyledInput
            type='input'
            name='contactName'
            id='contactName'
            onChange={formik.handleChange}
            value={formik.values.contactName}
            label={t('NAME')}
            feedbackMessage={
              formik.touched.contactName && formik.errors.contactName
            }
            feedbackStyle='invalid'
            setFieldValue={formik.setFieldValue}
          />

          <br/>

          <SimpleLabel>{t('PREFERRED CONTACT TYPE')}</SimpleLabel>
        </Col>

        <Col xs={12} md={6}>

          <ToggleSwitchWithLabel
            theme={toggleSwitchWithLabelTheme}
            label={t('EMAIL')}
            isChecked={formik.values.inquiryEmail}
            handleToggleChange={() => {
              formik.setFieldValue('inquiryEmail', !formik.values.inquiryEmail);
              formik.setFieldValue('inquiryTel', false);
            }}
          />

          <StyledInput
            type='input'
            name='contactEmail'
            id='contactEmail'
            onChange={(e) => {
              formik.handleChange(e)
            }}
            value={formik.values.contactEmail}
            label={t('EMAIL ADDRESS')}
            feedbackMessage={
              formik.touched.contactEmail && formik.errors.contactEmail
            }
            feedbackStyle='invalid'
            setFieldValue={formik.setFieldValue}
          />
        </Col>

        <Col xs={12} md={6}>

          <ToggleSwitchWithLabel
            theme={toggleSwitchWithLabelTheme}
            label={t('TELEPHONE')}
            isChecked={formik.values.inquiryTel}
            handleToggleChange={() => {
              formik.setFieldValue('inquiryTel', !formik.values.inquiryTel);
              formik.setFieldValue('inquiryEmail', false);
            }}
          />

          <StyledInput
            type='input'
            name='contactTel'
            id='contactTel'
            onChange={(e) => {
              formik.handleChange(e)
            }}
            value={formik.values.contactTel}
            label={t('TELEPHONE NUMBER')}
            feedbackMessage={
              formik.touched.contactTel && formik.errors.contactTel
            }
            feedbackStyle='invalid'
            setFieldValue={formik.setFieldValue}
          />

          <br/>
        </Col>

      </Row>
      <Row justify='center' style={{marginBottom: '1rem'}}>
        <Col xs='content'>
          {submitStatus.status && (
            <SimpleNote
              noteStatus={submitStatus.status}
              text={submitStatus.message}
            />
          )}
        </Col>
      </Row>


      <Navigator currentStep={currentStep} maxSteps={maxSteps}
                 prevStep={prevStep}/>
    </form>
  )
}

CustomerInput.propTypes = {
  savedValues: PropTypes.object,
  saveFormInputs: PropTypes.func.isRequired,
  prevStep: PropTypes.string,
  nextStep: PropTypes.string,
}

export default CustomerInput
